import { useEffect, useId, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Icon, Input, Text, useKeyboardSelectableList } from '@nula/core-ui';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import type { SearchHit } from '@/lib/search/search-service';
import { useDocumentSearch } from '../hooks/use-document-search';
import styles from './surface-document-picker-face.module.css';

export interface PickedDocument {
  id: string;
  title: string;
}

interface SurfaceDocumentPickerFaceProps {
  /** Documents never offered: this one and those already added. */
  exclude: ReadonlySet<string>;
  onPick: (document: PickedDocument) => void;
  onBack: () => void;
}

const NO_HITS: readonly SearchHit[] = [];
const RECENT_LIMIT = 5;
const RESULT_LIMIT = 8;
const LIST_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter']);

const back = cva(styles.back);
const option = cva(styles.option, {
  variants: {
    highlighted: { true: styles.highlighted },
  },
});

function titleOf(title: string): string {
  return title.trim() === '' ? 'Untitled' : title;
}

/** Best first in, best last out: the list reads top-down toward the field. */
function nearestLast(documents: readonly PickedDocument[], limit: number): PickedDocument[] {
  return documents.slice(0, limit).reverse();
}

export function SurfaceDocumentPickerFace({ exclude, onPick, onBack }: SurfaceDocumentPickerFaceProps) {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const workspaceDocs = useWorkspaceDocs();
  const search = useDocumentSearch(query);
  const hits = search.status === 'ready' ? search.hits : NO_HITS;

  const rows = useMemo<PickedDocument[]>(() => {
    const candidates = searching
      ? hits
      : workspaceDocs.filter((d) => d.kind === 'document' && !d.isArchived);
    const offered = candidates
      .filter((d) => !exclude.has(d.id))
      .map((d) => ({ id: d.id, title: titleOf(d.title) }));
    return nearestLast(offered, searching ? RESULT_LIMIT : RECENT_LIMIT);
  }, [searching, hits, workspaceDocs, exclude]);
  const bestIndex = Math.max(rows.length - 1, 0);

  const { highlightedIndex, setHighlightedIndex, getItemProps, getListProps } =
    useKeyboardSelectableList({ items: rows, initialIndex: bestIndex, onSelect: (row) => onPick(row) });

  // Reset during render, not in an effect, so aria-activedescendant never
  // names an option from the previous list for a frame.
  const rowsKey = rows.map((row) => row.id).join(' ');
  const [highlightedFor, setHighlightedFor] = useState(rowsKey);
  if (highlightedFor !== rowsKey) {
    setHighlightedFor(rowsKey);
    setHighlightedIndex(bestIndex);
  }

  const optionId = (index: number) => `${baseId}-option-${index}`;
  const highlightedId = rows.length > 0 ? optionId(highlightedIndex) : undefined;

  useEffect(() => {
    if (highlightedId) document.getElementById(highlightedId)?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedId]);

  const { onKeyDown: onListKeyDown } = getListProps();

  // Focus never leaves the field, so every key it handles has to stop here:
  // the panel's roving-focus handler would otherwise also act on it.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (query !== '') setQuery('');
      else onBack();
      return;
    }
    if (!LIST_KEYS.has(e.key)) return;
    e.stopPropagation();
    onListKeyDown(e);
  };

  const status = !searching ? null
    : search.status === 'loading' ? 'Searching…'
      : search.status === 'error' ? "Couldn't search documents. Try again."
        : rows.length === 0 ? `No documents match "${query}"`
          : null;

  return (
    <Flex vertical className={styles.pickerFace}>
      <button
        type="button"
        role="menuitem"
        aria-label="Back to the template"
        onClick={onBack}
        className={back()}
      >
        <Flex align="center" justify="center" className={styles.backIcon} aria-hidden="true">
          <Icon name="arrow_back" size={16} />
        </Flex>
        <Text className={styles.title}>Add a document</Text>
      </button>

      <Flex vertical className={styles.scroll}>
        <Flex vertical className={styles.stack}>
          {!searching && rows.length > 0 && (
            <Text className={styles.label} aria-hidden="true">Recent</Text>
          )}
          {status && <Text type="secondary" role="status" className={styles.status}>{status}</Text>}
          <Flex vertical id={listId} role="listbox" aria-label="Documents">
            {rows.map((row, i) => {
              const highlighted = i === highlightedIndex;
              return (
                <Flex
                  key={row.id}
                  id={optionId(i)}
                  align="center"
                  {...getItemProps(i)}
                  aria-label={row.title}
                  className={option({ highlighted })}
                >
                  <Icon name={getEntityKindMeta('document').icon} size={16} className={styles.kindIcon} />
                  <Text className={styles.name}>{row.title}</Text>
                  {highlighted && <Text className={styles.enter}>↵</Text>}
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Flex>

      <Input
        autoFocus
        role="combobox"
        aria-label="Search documents"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={highlightedId}
        placeholder="Search documents"
        prefix={<Icon name="search" size={16} />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        className={styles.search}
      />
    </Flex>
  );
}
